import React, {useEffect, ReactNode} from 'react';
import {useNavigate} from 'react-router-dom';
import Form from 'react-bootstrap/Form';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import {useFormik} from 'formik';
import {deserialize} from '@/views/storyboard/utils/helpers';
import EventFiltersBundle from '@/components/event_filters/EventFiltersBundle';
import {useStoreActions, useStoreState} from 'store';
import {StoryboardFilterSet} from '@/entities/chartFilters';


interface Props {
    alwaysUpdateReduxQueryParams?: boolean;
    widgetsResetKey?: string;
    enableReinitializeDefault?: boolean;
    saveButtonContent?: ReactNode;
}

const SmartFilters = React.memo<Props>((
    {
        alwaysUpdateReduxQueryParams = false,
        widgetsResetKey = '',
        enableReinitializeDefault = true,
        saveButtonContent,
    },
) => {
    const navigate = useNavigate();
    const storeSearchQueryAllFields = useStoreActions((state) => state.search.setSearchQueryAllFields);
    const storeSearchQueryAdditionalFilters = useStoreActions((state) => state.search.setSearchQueryAdditionalFilters);

    const activeStoryboard = useStoreState((state) => state.activeStoryboard.activeStoryboard);

    const paramsSetter = useStoreState((actions) => actions.search.paramsSetter);

    const queryParams = deserialize(new URLSearchParams(window.location.search));

    let initialFormAdditionalFilters = JSON.parse(queryParams.additionalFilters || '[]');
    if (
        !initialFormAdditionalFilters.length
        && activeStoryboard?.globalFilters?.additional_filters?.length
    ) {
        initialFormAdditionalFilters = activeStoryboard.globalFilters.additional_filters;
    }

    let initialFormAllFields = JSON.parse(queryParams.all_fields || '{}');
    if (
        !Object.keys(initialFormAllFields).length
        && activeStoryboard?.globalFilters?.all_fields
        && Object.keys(activeStoryboard.globalFilters.all_fields).length
    ) {
        initialFormAllFields = activeStoryboard.globalFilters.all_fields;
    }

    const formik = useFormik<StoryboardFilterSet>({
        initialValues: {
            additionalFilters: initialFormAdditionalFilters,
            all_fields: initialFormAllFields,
        },
        enableReinitialize: enableReinitializeDefault,
        // this form is autosubmitted so onSubmit is pointless, but required by Formik
        onSubmit: () => {
        },
    });

    useEffect(() => {
        const searchParams = new URLSearchParams(window.location.search);
        const hasFilterParams = searchParams.has('all_fields') || searchParams.has('additionalFilters');
        const isSearchPage = window.location.href.includes(window.URLS.searchUrl);

        if (!isSearchPage && hasFilterParams) {
            searchParams.delete('all_fields');
            searchParams.delete('additionalFilters');
            const nextSearch = searchParams.toString();
            navigate(nextSearch ? `${window.location.pathname}?${nextSearch}` : window.location.pathname, {replace: true});
        }
        formik.resetForm();
    }, [
        activeStoryboard?.id,
    ]);

    useEffect(() => {
        if (paramsSetter || alwaysUpdateReduxQueryParams) {
            storeSearchQueryAllFields(formik.values.all_fields);
            storeSearchQueryAdditionalFilters(formik.values.additionalFilters);
        }
    }, [
        formik.values.additionalFilters,
        formik.values.all_fields,
        paramsSetter,
        storeSearchQueryAdditionalFilters,
        storeSearchQueryAllFields,
    ]);

    const smartFilterBarContent = (
        <Form noValidate onSubmit={formik.handleSubmit}>
            <Row id='storyboard-filters'>
                <Col xs={12}>
                    <EventFiltersBundle<StoryboardFilterSet>
                        widgetsResetKey={widgetsResetKey}
                        setValues={formik.setValues}
                        setFieldValue={formik.setFieldValue}
                        formValues={formik.values}
                        addFiltersLabelText=''
                        parent='smart-filter-sidebar'
                        enableCollapse={true}
                        saveButtonContent={saveButtonContent}
                    />
                </Col>
            </Row>
        </Form>
    );

    return (
        <>
            <div>
                {smartFilterBarContent}
            </div>
        </>
    );
});

export default SmartFilters;
